import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, ArrowRight, CalendarDays, FileCheck2, ClipboardCheck, Sparkles, CheckCircle2, ShieldCheck, KeyRound, Clock } from "lucide-react";
import { PLAN_LABELS, currency } from "@/lib/format";
import { computePricing } from "@/lib/pricing";

const steps = [
  { key: "dates", label: "Dates and plan", icon: CalendarDays },
  { key: "docs", label: "Documents", icon: FileCheck2 },
  { key: "review", label: "Review", icon: ClipboardCheck },
];

export default function BookingFlowPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  const today = new Date();
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const dayAfter = new Date(today); dayAfter.setDate(today.getDate() + 4);

  // Support datetime-local values from the planner (?start=YYYY-MM-DDTHH:mm)
  const initialStart = searchParams.get("start") || tomorrow.toISOString().slice(0, 10);
  const initialEnd = searchParams.get("end") || dayAfter.toISOString().slice(0, 10);
  const initialPlan = searchParams.get("plan") || "daily";

  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [planType, setPlanType] = useState(initialPlan);
  const [documentsUploaded, setDocumentsUploaded] = useState(false);
  const [licenseName, setLicenseName] = useState("");
  const [businessUse, setBusinessUse] = useState(false);
  const [maintenance, setMaintenance] = useState("alfa_managed");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) { nav(`/login?next=/vehicles/${id}/reserve`); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/vehicles/${id}`);
        setVehicle(res.data);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Determine if inputs are datetime-local (from planner) vs plain date
  const isHourly = planType === "hourly";
  const inputType = /T\d{2}:/.test(startDate) || isHourly ? "datetime-local" : "date";

  // Use the shared pricing engine so figures match the planner exactly.
  const pricing = useMemo(() => {
    if (!vehicle) return null;
    const startISO = /T/.test(startDate) ? startDate : `${startDate}T10:00`;
    const endISO = /T/.test(endDate) ? endDate : `${endDate}T10:00`;
    return computePricing({ vehicle, startISO, endISO, isMember: !!user?.is_member, forceHourly: isHourly });
  }, [vehicle, startDate, endDate, user, isHourly]);

  const days = pricing?.days || 0;

  const totals = useMemo(() => {
    if (!pricing?.ok) return null;
    return {
      std: pricing.standard?.total || 0,
      mem: pricing.member?.total || 0,
      used: pricing.totalUsed || 0,
      savings: pricing.savings || 0,
      isMember: !!user?.is_member,
    };
  }, [pricing, user]);

  const canProceed = () => {
    if (step === 0) {
      if (!startDate || !endDate) return false;
      if (planType.startsWith("rtl_") && !user?.is_member) return false;
      if (businessUse && !user?.is_member) return false;
      if (!pricing?.ok) return false;
      return true;
    }
    if (step === 1) return documentsUploaded;
    return true;
  };

  const submit = async () => {
    if (!vehicle) return;
    setSubmitting(true);
    try {
      // Convert to date-only strings for the current backend contract.
      const payload = {
        vehicle_id: vehicle.id,
        plan_type: isHourly ? "daily" : planType,
        start_date: (startDate || "").slice(0, 10),
        end_date: (endDate || "").slice(0, 10),
        documents_uploaded: documentsUploaded,
        maintenance_option: planType === "rtl_12" ? maintenance : null,
        business_use: businessUse,
      };
      const res = await axios.post(`${API}/bookings`, payload);
      toast.success(`Reservation created (${res.data.booking_ref})`);
      nav(`/portal?tab=bookings&new=${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create reservation");
    } finally { setSubmitting(false); }
  };

  if (loading || !vehicle) {
    return <div className="container-x section-y"><p className="text-muted-foreground">Loading reservation…</p></div>;
  }

  return (
    <div className="bg-background" data-testid="booking-page">
      <div className="container-x pt-8 pb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => nav(-1)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <div className="text-xs text-muted-foreground">Reservation · {vehicle.name}</div>
      </div>

      <div className="container-x pb-14 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="card-clean p-5" data-testid="booking-flow-stepper">
            <div className="flex items-center gap-3 flex-wrap">
              {steps.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? "bg-[hsl(var(--primary))] text-white" : "bg-secondary text-muted-foreground"}`}>{i + 1}</div>
                  <div className={`text-sm ${i === step ? "font-semibold" : "text-muted-foreground"}`}>{s.label}</div>
                  {i < steps.length - 1 && <div className="w-8 h-px bg-border" />}
                </div>
              ))}
            </div>
            <Progress value={((step + 1) / steps.length) * 100} className="mt-4" />
          </div>

          <div className="card-clean p-6 mt-5">
            {step === 0 && (
              <div>
                <h2 className="font-display text-xl font-semibold">Choose dates and plan</h2>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Pick up</Label>
                    <Input type={inputType} value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="booking-start-date-input" />
                  </div>
                  <div>
                    <Label>Return</Label>
                    <Input type={inputType} value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="booking-end-date-input" />
                  </div>
                  <div>
                    <Label>Plan type</Label>
                    <Select value={planType} onValueChange={setPlanType}>
                      <SelectTrigger data-testid="booking-plan"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {user?.is_member && (
                          <SelectItem value="hourly">Hourly — members only</SelectItem>
                        )}
                        {Object.entries(PLAN_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k} disabled={k.startsWith("rtl_") && !user?.is_member}>
                            {v}{k.startsWith("rtl_") && !user?.is_member ? " — members only" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {planType.startsWith("rtl_") && !user?.is_member && (
                      <p className="text-xs text-[hsl(var(--danger))] mt-1">Rental Term Leasing requires Drive Alfa Club membership.</p>
                    )}
                    {!pricing?.ok && pricing?.error && (
                      <p className="text-xs text-[hsl(var(--danger))] mt-1" data-testid="booking-plan-error">{pricing.error}</p>
                    )}
                  </div>
                  <div>
                    <Label>Duration</Label>
                    <Input readOnly value={
                      pricing?.mode === "hourly"
                        ? `${pricing.hours} hour${pricing.hours > 1 ? "s" : ""}`
                        : `${days} day${days > 1 ? "s" : ""}`
                    } />
                  </div>
                </div>

                {planType === "rtl_12" && (
                  <div className="mt-6">
                    <Label>Maintenance option (12-month Rental Term Leasing)</Label>
                    <RadioGroup value={maintenance} onValueChange={setMaintenance} className="mt-2 grid sm:grid-cols-2 gap-3">
                      <label className="card-clean p-4 flex items-start gap-3 cursor-pointer">
                        <RadioGroupItem value="alfa_managed" data-testid="maintenance-alfa" />
                        <div>
                          <div className="font-medium">AlfaDrive-Managed</div>
                          <div className="text-xs text-muted-foreground">We handle scheduled service. Approximately +$160/month.</div>
                        </div>
                      </label>
                      <label className="card-clean p-4 flex items-start gap-3 cursor-pointer">
                        <RadioGroupItem value="member_managed" data-testid="maintenance-member" />
                        <div>
                          <div className="font-medium">Member-Managed</div>
                          <div className="text-xs text-muted-foreground">You handle approved maintenance. Save up to 25%.</div>
                        </div>
                      </label>
                    </RadioGroup>
                  </div>
                )}

                <div className="mt-6">
                  <label className="flex items-start gap-2">
                    <Checkbox checked={businessUse} onCheckedChange={(v) => setBusinessUse(!!v)} disabled={!user?.is_member} data-testid="booking-business-use" />
                    <span className="text-sm">
                      Use this vehicle for business (Alfa One Path — business/fleet program)
                      {!user?.is_member && <span className="block text-xs text-[hsl(var(--danger))]">Requires Drive Alfa Club membership.</span>}
                    </span>
                  </label>
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="font-display text-xl font-semibold">Upload required documents</h2>
                <p className="text-sm text-muted-foreground mt-1">Upload your driver’s licence to complete verification. For the current MVP you can enter a placeholder file name.</p>
                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Driver’s licence file (placeholder)</Label>
                    <Input value={licenseName} onChange={(e) => setLicenseName(e.target.value)} placeholder="e.g. drivers_license.jpg" data-testid="booking-license-input" />
                  </div>
                  <div className="card-clean p-4">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" />
                      <span className="text-sm font-medium">We securely track your documents</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">You can also upload files anytime from your Customer Portal.</p>
                  </div>
                </div>
                <label className="mt-5 inline-flex items-center gap-2 text-sm">
                  <Checkbox checked={documentsUploaded} onCheckedChange={(v) => setDocumentsUploaded(!!v)} data-testid="booking-docs-checkbox" />
                  I confirm my driver’s licence is ready and accurate.
                </label>
              </div>
            )}

            {step === 2 && totals && (
              <div>
                <h2 className="font-display text-xl font-semibold">Review your reservation</h2>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div className="card-clean p-4">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Vehicle</div>
                    <div className="font-display text-lg font-semibold">{vehicle.name}</div>
                    <div className="text-xs text-muted-foreground">{vehicle.brand}</div>
                  </div>
                  <div className="card-clean p-4">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">When</div>
                    <div className="font-medium">{startDate} → {endDate}</div>
                    <div className="text-xs text-muted-foreground">{days} day{days > 1 ? "s" : ""} · {PLAN_LABELS[planType]}</div>
                  </div>
                </div>
                <div className="card-clean p-5 mt-4">
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Standard total</span><span className={`${totals.isMember ? "line-through text-muted-foreground" : "font-medium"} tabular-nums`}>{currency(totals.std)}</span></div>
                  {totals.isMember && (<div className="flex items-center justify-between text-sm mt-1"><span className="text-muted-foreground">Member total</span><span className="font-display text-xl font-semibold tabular-nums">{currency(totals.mem)}</span></div>)}
                  {!totals.isMember && (<div className="flex items-center justify-between text-sm mt-1"><span className="text-muted-foreground">Total</span><span className="font-display text-xl font-semibold tabular-nums">{currency(totals.std)}</span></div>)}
                  {totals.isMember && totals.savings > 0 && (<div className="mt-2 rounded-lg badge-blue px-3 py-2 text-xs inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> You’ll save {currency(totals.savings)} as a member.</div>)}
                  <Separator className="my-3" />
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Refundable deposit</span><span className="tabular-nums">{currency(vehicle.deposit)}</span></div>
                </div>
                <div className="mt-4 card-clean p-4 flex items-start gap-3">
                  <KeyRound className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    By confirming, you agree to AlfaDrive’s rental terms preview and consent to identity verification. Payment and documents remain in review status pending admin approval.
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="booking-flow-back-button">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              {step < steps.length - 1 ? (
                <Button className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" disabled={!canProceed()} onClick={() => setStep((s) => s + 1)} data-testid="booking-flow-next-button">
                  Continue <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" disabled={submitting} onClick={submit} data-testid="booking-flow-confirm-button">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm reservation
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-24 card-clean overflow-hidden">
            <div className="aspect-[16/10] bg-[hsl(var(--charcoal-100))] flex items-center justify-center">
              <img src={vehicle.image_url} alt={vehicle.name} className={`w-full h-full ${((vehicle.image_url || "").toLowerCase().includes("remove_bg") || (vehicle.image_url || "").toLowerCase().includes("curbo")) ? "object-contain p-6 drop-shadow-[0_18px_22px_rgba(15,23,42,0.14)]" : "object-cover"}`} onError={(e) => { e.currentTarget.src = "/brand/alfadrive-lion-transparent.png"; }} />
            </div>
            <div className="p-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{vehicle.brand}</div>
              <div className="font-display text-lg font-semibold">{vehicle.name}</div>
              <Separator className="my-3" />
              {totals && (
                <>
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Standard</span><span className={`${totals.isMember ? "line-through text-muted-foreground" : "font-medium"} tabular-nums`}>{currency(totals.std)}</span></div>
                  {totals.isMember && (<div className="flex items-center justify-between text-sm mt-1"><span className="text-muted-foreground">Member</span><span className="font-medium tabular-nums">{currency(totals.mem)}</span></div>)}
                  {totals.isMember && (<div className="mt-2 rounded-lg badge-blue px-3 py-2 text-[11px]">You save {currency(totals.savings)}</div>)}
                  {!totals.isMember && (<div className="mt-2 text-[11px] text-muted-foreground">Join Drive Alfa Club to unlock member pricing on future bookings.</div>)}
                </>
              )}
              <Separator className="my-3" />
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Refundable deposit</span><span className="tabular-nums">{currency(vehicle.deposit)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
